import { rfc3339TimestampToEpochMilliseconds } from "./portfolio/schema";
import type { QuoteObservationSnapshot } from "./types";

const KOREA_TIME_ZONE = "Asia/Seoul";

const KST_DATE_TIME_FORMATTER = new Intl.DateTimeFormat("en-US", {
  calendar: "gregory",
  numberingSystem: "latn",
  timeZone: KOREA_TIME_ZONE,
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

/**
 * Formats a strict RFC 3339 provider clock as a compact, locale-independent
 * Korean-time label. Invalid or ambiguous timestamps are deliberately omitted
 * instead of falling back to the browser's local timezone.
 */
export function formatQuoteObservedAtKst(value: unknown): string | null {
  const milliseconds = rfc3339TimestampToEpochMilliseconds(value);
  if (milliseconds === null) {
    return null;
  }

  const parts = new Map(
    KST_DATE_TIME_FORMATTER
      .formatToParts(new Date(milliseconds))
      .map(({ type, value: partValue }) => [type, partValue]),
  );
  const month = parts.get("month");
  const day = parts.get("day");
  const hour = parts.get("hour");
  const minute = parts.get("minute");
  if (!month || !day || !hour || !minute) {
    return null;
  }

  return `${month}/${day} ${hour}:${minute} KST`;
}

/**
 * Selects the newest valid provider observation without mutating or repairing
 * any source value. Invalid clocks are ignored.
 */
export function latestQuoteObservation(
  observations: readonly (QuoteObservationSnapshot | undefined)[],
): QuoteObservationSnapshot | undefined {
  let latest: QuoteObservationSnapshot | undefined;
  let latestMilliseconds = Number.NEGATIVE_INFINITY;

  for (const observation of observations) {
    if (!observation) {
      continue;
    }
    const milliseconds = rfc3339TimestampToEpochMilliseconds(
      observation.observedAt,
    );
    if (milliseconds !== null && milliseconds > latestMilliseconds) {
      latest = observation;
      latestMilliseconds = milliseconds;
    }
  }

  return latest;
}

/**
 * Builds portfolio-level display provenance without pretending that one
 * holding's market is the as-of market for the whole weighted return.
 */
export function latestPortfolioQuoteObservation(
  observations: readonly (QuoteObservationSnapshot | undefined)[],
  valuationCurrency: string,
): QuoteObservationSnapshot | undefined {
  const latest = latestQuoteObservation(observations);
  return latest
    ? {
        ...latest,
        includesUsMarket: observations.some(
          (observation) => observation?.market === "US",
        ),
        valuationCurrency,
      }
    : undefined;
}

export function quoteCoverageLabel(
  market: QuoteObservationSnapshot["market"],
): string {
  if (market === "US") {
    return "미국장 · 장전/애프터 조회 지원";
  }
  if (market === "KRX") {
    return "한국 정규장 시세";
  }
  return "최신 시세";
}

export function quoteComparisonLabel(
  market: QuoteObservationSnapshot["market"],
): string {
  if (market === "US" || market === "KRX") {
    return "전일 종가 대비";
  }
  return "직전 종가 대비";
}
